import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Skeleton, type DialogShellFooterProps } from '@bliks-io/ui';
import { MergeAssignmentConfirmDialog } from './MergeAssignmentConfirmDialog';
import { ReviewAlert } from '@/components/processes/ReviewAlert';
import { ReviewModalLayout } from '@/components/processes/ReviewModalLayout';
import { ReviewUserSection } from '@/components/processes/ReviewUserSection';
import { ReviewProcessDetails } from '@/components/processes/ReviewProcessDetails';
import { StepPreviewWithProcessMap } from '@/components/processes/StepPreviewWithProcessMap';
import {
  StepRecordingSelect,
  type StepSelectOption
} from '@/components/processes/StepRecordingSelect';
import { useReviewModal } from '@/hooks/useReviewModal';
import {
  useAcceptAssignment,
  useRejectAssignment,
  useMergeAssignment
} from '@/api/mutations/useAssignments';
import { useMergeCandidates } from '@/api/queries/useMergeCandidates';
import type {
  AssigneeModel,
  AssignmentUser,
  AssignmentProcess,
  MergeCandidate,
  ProcessStepAssignment
} from '@bliks-io/contracts';
import { ArrowRight, X, Handshake, GitMerge } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { ReassignStepContent } from './ReassignStepContent';
import { SuccessStepContent } from './SuccessStepContent';
import type { ReassignPayload } from '@/lib/successorEntityMapping';
import { isStartAnchoredSource } from '@/lib/assignment-source';
import { getProcessId } from '../utils/assignmentHelpers';

interface AssignmentReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: ProcessStepAssignment | null;
}

/**
 * Hint for non-user assignees: the recipient documents on the entity's behalf
 * (System → system owner, ExternalContact → the nominated recording delegate,
 * falling back to the process owner while none is set), so remind them to
 * describe what the entity does rather than what they do themselves.
 */
function AssigneeEntityHint({ assignedToModel }: { assignedToModel: AssigneeModel }) {
  const { t } = useTranslation();

  if (assignedToModel === 'System') {
    return (
      <ReviewAlert
        variant="info"
        title={t('assignmentReview.systemAssigneeHintTitle')}
        description={t('assignmentReview.systemAssigneeHintDescription')}
      />
    );
  }
  if (assignedToModel === 'ExternalContact') {
    return (
      <ReviewAlert
        variant="info"
        title={t('assignmentReview.externalContactAssigneeHintTitle')}
        description={t('assignmentReview.externalContactAssigneeHintDescription')}
      />
    );
  }
  return null;
}

type Step = 'review' | 'reassign' | 'success';
type SuccessType = 'accepted' | 'rejected';

export function AssignmentReviewModal({
  open,
  onOpenChange,
  assignment
}: AssignmentReviewModalProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const navigate = useNavigate();

  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);

  const resetMergeState = useCallback(() => {
    setSelectedCandidateId(null);
    setMergeConfirmOpen(false);
  }, []);

  // Wrap onOpenChange so merge state always resets when the modal closes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) resetMergeState();
      onOpenChange(newOpen);
    },
    [onOpenChange, resetMergeState]
  );

  const { step, setStep, successType, setSuccessType, error, setError, handleFinish } =
    useReviewModal<Step, SuccessType>(open, {
      initialStep: 'review',
      initialSuccessType: 'accepted',
      onClose: handleOpenChange
    });

  const acceptMutation = useAcceptAssignment();
  const rejectMutation = useRejectAssignment();
  const mergeMutation = useMergeAssignment();

  const { data: mergeCandidatesData, isLoading: isLoadingMergeCandidates } = useMergeCandidates(
    open ? assignment?._id : undefined
  );
  const mergeCandidates = useMemo(
    () => mergeCandidatesData?.candidates ?? [],
    [mergeCandidatesData?.candidates]
  );

  const selectedCandidate = useMemo(
    () => mergeCandidates.find((c) => c._id === selectedCandidateId) ?? null,
    [mergeCandidates, selectedCandidateId]
  );

  const mergeCandidateOptions = useMemo<StepSelectOption[]>(
    () =>
      mergeCandidates.map((c) => ({
        value: c._id,
        label: c.generatedName || t('assignmentReview.unnamedStep'),
        description: c.generatedDescription,
        metadata: c.output
          ? t('viralRecording.modals.inputModal.outputLabel', { output: c.output })
          : undefined,
        actionType: c.actionType
      })),
    [mergeCandidates, t]
  );

  // Get assignment details
  const assignmentDetails = useMemo(() => {
    if (!assignment) return null;

    const process = assignment.process as AssignmentProcess;
    const assignedBy =
      typeof assignment.assignedBy === 'object' && assignment.assignedBy !== null
        ? (assignment.assignedBy as AssignmentUser)
        : null;
    // `assignedTo` is widened to also allow a bare `{ type, id }` union ref, but
    // suggestion placeholders never reach my-tasks (list-tasks serves only the
    // entity arm), so narrow back to the populated entity (carries `_id`).
    const assignedTo =
      typeof assignment.assignedTo === 'object' &&
      assignment.assignedTo !== null &&
      '_id' in assignment.assignedTo
        ? assignment.assignedTo
        : undefined;
    // Start-anchored in either era: legacy 'START' marker or populated
    // start event node (issue 11).
    const isFirstAssignment = isStartAnchoredSource(assignment.sourceProcessStep);

    return {
      process,
      processId: getProcessId(assignment.process),
      // Pre-accept there is no recording yet, so the map highlights the
      // published node the invitation points at. Accepting mints a recording
      // with `_id === stableStepId`, so the id stays valid on the other side
      // of the accept; pre-`stableStepId` legacy rows fall back to their own
      // id, which matches no node (silent no-op).
      highlightNodeId: assignment.stableStepId || assignment._id,
      assignedBy,
      assignedTo,
      assignedToModel: assignment.assignedToModel,
      isFirstAssignment
    };
  }, [assignment]);

  // Handle accept
  const handleAccept = async () => {
    if (!assignment) return;

    try {
      setError(undefined);
      const result = await acceptMutation.mutateAsync({
        assignmentId: assignment._id,
        processId: getProcessId(assignment.process)
      });

      // Navigate directly to the step recording page
      if (result.processStepRecordingId) {
        handleOpenChange(false);
        navigate({
          to: '/$slug/processes/recording/step-recording/$recordingId',
          params: { slug, recordingId: result.processStepRecordingId }
        });
      } else {
        // Fallback to success screen if no recording ID
        setSuccessType('accepted');
        setStep('success');
      }
    } catch (err) {
      console.error('Failed to accept assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept assignment');
    }
  };

  // Handle merge into existing step
  const handleMerge = async (candidate: MergeCandidate) => {
    if (!assignment) return;

    try {
      setError(undefined);
      await mergeMutation.mutateAsync({
        assignmentId: assignment._id,
        processId: getProcessId(assignment.process),
        data: { targetStepRecordingId: candidate._id }
      });
      setMergeConfirmOpen(false);
      handleOpenChange(false);
    } catch (err) {
      console.error('Failed to merge assignment:', err);
      setError(err instanceof Error ? err.message : t('assignmentReview.mergeError'));
    }
  };

  // Handle deny (go to reassign step)
  const handleDeny = () => {
    setStep('reassign');
  };

  // Handle reassign submission
  const submitReassign = async (params: ReassignPayload) => {
    if (!assignment) return;

    try {
      setError(undefined);
      await rejectMutation.mutateAsync({
        assignmentId: assignment._id,
        processId: getProcessId(assignment.process),
        data: {
          rejectReason: params.isUnclear ? 'unclear' : 're-assign',
          reAssigneeId: params.reAssigneeId,
          reAssigneeModel: params.reAssigneeModel,
          reAssigneeType: params.reAssigneeType
        }
      });
      setSuccessType('rejected');
      setStep('success');
    } catch (err) {
      console.error('Failed to reject assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject assignment');
    }
  };

  // Render reassign context dialog when in reassign step (assignment guaranteed
  // to exist here). Closing it (cancel / esc / outside-click) returns to the
  // review step rather than closing the whole flow, mirroring the prior "Back"
  // button behavior.
  if (step === 'reassign' && assignment) {
    return (
      <ReassignStepContent
        open={open}
        assignment={assignment}
        isSubmitting={rejectMutation.isPending}
        onReviewReturn={() => setStep('review')}
        onSubmitReassign={submitReassign}
      />
    );
  }

  // Render success screen when in success step (assignment guaranteed to exist at this point)
  if (step === 'success' && assignment) {
    return (
      <SuccessStepContent
        open={open}
        successType={successType}
        onOpenChange={handleOpenChange}
        onFinish={handleFinish}
      />
    );
  }

  const isBusy = acceptMutation.isPending || rejectMutation.isPending || mergeMutation.isPending;

  const reviewFooter: DialogShellFooterProps | undefined =
    assignment && assignmentDetails
      ? {
          secondary: {
            label: (
              <>
                <X className="h-4 w-4 mr-2" />
                {t('assignmentReview.deny')}
              </>
            ),
            onClick: handleDeny,
            disabled: isBusy
          },
          primary: selectedCandidate
            ? {
                label: (
                  <>
                    <GitMerge className="h-4 w-4 mr-2" />
                    {t('assignmentReview.mergeInto')}
                  </>
                ),
                onClick: () => setMergeConfirmOpen(true),
                disabled: mergeMutation.isPending
              }
            : {
                label: (
                  <span className="inline-flex items-center whitespace-nowrap">
                    {t('assignmentReview.accept')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </span>
                ),
                onClick: handleAccept,
                loading: acceptMutation.isPending,
                disabled: isBusy
              }
        }
      : undefined;

  // Always render the review dialog — visibility controlled by the open prop.
  // When assignment is null, open is false (from parent), so the dialog stays mounted but hidden.
  // When assignment is set, open transitions false→true, triggering the Radix overlay animation.
  return (
    <ReviewModalLayout
      open={open}
      onOpenChange={handleOpenChange}
      icon={Handshake}
      title={t('processPage.assignmentTitle')}
      description={t('processPage.assignmentDescription')}
      error={error}
      onDismissError={() => setError(undefined)}
      footer={reviewFooter}
    >
      {assignment && assignmentDetails && (
        <>
          <AssigneeEntityHint assignedToModel={assignmentDetails.assignedToModel} />

          {/* 1. Assigned By & Process Details (Two Column) */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:items-stretch">
            {assignmentDetails.assignedBy && (
              <div className="md:col-span-2">
                <ReviewUserSection
                  label={t('myTasks.assignedBy')}
                  user={assignmentDetails.assignedBy}
                />
              </div>
            )}
            <div className={assignmentDetails.assignedBy ? 'md:col-span-3' : 'col-span-full'}>
              <ReviewProcessDetails process={assignmentDetails.process} />
            </div>
          </div>

          {/* 2. Process Step */}
          <StepPreviewWithProcessMap
            label={
              assignmentDetails.isFirstAssignment
                ? t('myTasks.processStart')
                : t('assignmentReview.previousStep', { defaultValue: 'Previous Step' })
            }
            sourceProcessStep={assignment.sourceProcessStep}
            assignedTo={assignmentDetails.assignedTo}
            assignedToModel={assignmentDetails.assignedToModel}
            ruleIds={assignment.ruleIds}
            hostOpen={open}
            processId={assignmentDetails.processId}
            highlightNodeId={assignmentDetails.highlightNodeId}
          />

          <MergeCandidatesSection
            isLoading={isLoadingMergeCandidates}
            options={mergeCandidateOptions}
            value={selectedCandidateId}
            onValueChange={setSelectedCandidateId}
          />

          {/* Merge Confirmation Dialog */}
          <MergeAssignmentConfirmDialog
            open={mergeConfirmOpen}
            onOpenChange={setMergeConfirmOpen}
            stepName={selectedCandidate?.generatedName || t('assignmentReview.unnamedStep')}
            isMerging={mergeMutation.isPending}
            onConfirm={() => {
              if (selectedCandidate) handleMerge(selectedCandidate);
            }}
          />
        </>
      )}
    </ReviewModalLayout>
  );
}

function MergeCandidatesSection({
  isLoading,
  options,
  value,
  onValueChange
}: {
  isLoading: boolean;
  options: StepSelectOption[];
  value: string | null;
  onValueChange: (value: string | null) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton role="status" aria-label={t('common.loading')} className="h-10" />;
  }
  if (options.length === 0) return null;

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-medium">{t('assignmentReview.mergeSectionTitle')}</h4>
        <p className="text-xs text-muted-foreground">
          {t('assignmentReview.mergeSectionDescription')}
        </p>
      </div>
      <StepRecordingSelect
        options={options}
        value={value}
        onValueChange={onValueChange}
        placeholder={t('assignmentReview.selectMergeCandidate')}
        searchPlaceholder={t('assignmentReview.searchMergeCandidates')}
        emptyMessage={t('assignmentReview.noMergeCandidates')}
      />
    </div>
  );
}
